import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Cookies } from 'react-cookie';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  localize,
  Logger,
  usePendo,
  useResponsiveValueRedux,
} from '@saviynt/common';
import {
  AlertBannerProvider,
  Box,
  Button,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import Lottie from 'lottie-react';
import momentTimezone from 'moment-timezone';

import LandscapeModeIcon from '../../ECMv6/assets/images/lotties/landscapemode-loader.json';
import { ACTION_BUTTON_KINDS } from '../../components/CertsActionButtons/constant';
import CertsSidePanel from '../../components/CertsDetails/CertsSidePanel';
import { BY_DECISION } from '../../components/CertsDetails/PopoverProgressBreakdown';
import {
  TAB_STATUS_ALL,
  TAB_STATUS_CONS,
  TAB_STATUS_DONE,
  TAB_STATUS_TODO,
} from '../../components/ViewControls';
import {
  checkAllPersonalizedRowsExist,
  EO_GROUP_BY_CERT_ENTITLEMENT_VALUEKEY,
  EO_UPDATE_ROW_FILTER_MAPPING,
  EO_UPDATE_ROW_FILTER_MAPPING_WITH_OBJECTS_TO_BE_INCLUDED,
  UPDATE_ROW_FILTER_MAPPING,
  UPDATE_ROW_FILTER_MAPPING_WITH_OBJECTS_TO_BE_INCLUDED,
} from '../../components/ViewControls/RowsFilter';
import { parseStringValue } from '../../components/ViewControls/RowsFilter/utils';
import {
  checkEsConnection,
  clearCertsSession,
  fetchBulkActionProgress,
  getCertDetailsData,
  getColumnPickerListDetails,
  getPersonalization,
  getPredefinedComments,
  getProgressBreakdownDetails,
  getRefreshedCertDetails,
  getTabCounts,
  getTotalCount,
  getViewDetails,
  setBannerForConsultAndReassign,
  setBulkActionAlert,
  setBulkActionFailed,
  setBulkActionInProgress,
  setCertKeyInUI,
  setCertsTimeZone,
  setCertTriggerFromSearchOrFilter,
  setDefaultRowFilters,
  setPersonalizationViewOption,
  setSelectedRowFilters,
  setSidePanelVisible,
  setTabHightLightKey,
  setTabsDisabled,
  setTriggerForUserDefaultView,
  triggerFilterChange,
  updatePersonalizationAll,
  updateScreenSize,
  updateTouchDevice,
} from '../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  getItemsApiOffset,
  MODAL_SIDEPANEL_DIRECTION,
  PERSONALIZATION_STATUS,
  SCREEN_WIDTH_QUERIES,
  VIEW_OPTIONS,
} from '../../utils/constant';
import { useInvertedLocalize } from '../../utils/localization';
import { useIsTouchViewportDevice } from '../../utils/use-istouchviewport';
import {
  executeBackNavigation,
  findHierarchyElementsV2,
  flattenList,
  getMessageKey,
} from '../../utils/utils';

import CertAlerts from './CertsAlerts/CertsAlerts';
import {
  checkIfValidNode,
  getTypeOfHierarchy,
  updateExistingNodes,
  updateExistingSingleNode,
  updateNodeWithTrackerId,
} from './CertsDetailsList/utils';
import CertsDetailsExportAlerts from './CertsExport/CertsDetailsExportAlerts';
import SaviCompanion from './SaviCompanion/SaviCompanion';
import CertsControlsGroup from './CertsControlsGroup';
import CertsDetailsList from './CertsDetailsList';
import CertsFooterActionBar from './CertsFooterActionBar';
import CertsHeader from './CertsHeader';
import invertedMessages from './messages';

import './CertificationDetails.css';

const cookies = new Cookies();
const AUTO_SHOW_SAVI_COMPANION = 'auto_show_savi_modal';
const PROMISE_REJECTED = 'rejected';

function CertificationDetails() {
  const intl = useIntl();
  const l = useInvertedLocalize(invertedMessages);
  const dispatch = useDispatch();
  const location = useLocation();

  // Pendo
  usePendo();
  // Savi
  const showSaviLocalCookie = cookies.get(AUTO_SHOW_SAVI_COMPANION);
  const showSaviLocal = !showSaviLocalCookie || showSaviLocalCookie === '1';
  const [showSavCompanion, setShowSaviCompanion] = useState(showSaviLocal);
  const [showSaviCompanionButtonPopup, setShowSaviCompanionButtonPopup] =
    useState(false);
  const [isMoreDataAvailable, setIsMoreDataAvailable] = useState(true);
  // View Controls
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState([]);
  const [isDraggingDndItem, setIsDraggingDndItem] = useState(false);
  // Sidepanel
  const [sidePanelData, setSidePanelData] = useState(null);
  const [sidePanelActionsData, setSidePanelActionsData] = useState(null);
  // Details List
  const [mergedListsAfterRowFilter, setMergedListsAfterRowFilter] = useState(
    []
  );
  const [mergedLists, setMergedLists] = useState([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [timeoutIds, setTimeoutIds] = useState([]);
  // Queue for handleAutoSelectParent
  const [autoSelectQueue, setAutoSelectQueue] = useState([]);
  const processingRef = useRef(false);

  // Error
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isClassicCampaignModalOpen, setIsClassicCampaignModalOpen] =
    useState(false);
  // Landscape mode and height below 660px
  const isTouchDevice = useIsTouchViewportDevice();
  const [isPotrait, setIsPotrait] = useState(true);
  const [isPotraitModalOpen, setIsPotraitModalOpen] = useState(false);

  const detailsListRef = useRef(null);
  /**
   * CR-21415: Tracks the range of fully visible tiles in the viewport.
   * @type {{ start: number, end: number }} start - 1-based index of first fully visible tile, end - 1-based index of last fully visible tile.
   */
  const [visibleItemRange, setVisibleItemRange] = useState({
    start: 1,
    end: 0,
  });
  const filterBarRef = useRef(null);
  const controlsRef = useRef(null);
  const certPageRef = useRef(null);
  const bulkActionPollingIntervalRef = useRef(null);
  const bulkActionPollingFailuresRef = useRef(0);
  const prevBulkInProgressRef = useRef(false);
  const {
    core: { columnNames, searchText, campaignType },
    global: { backNavigation, viewRowsList },
    personalization,
    saviCompanion: { isSaviEnabled },
    sidePanel: { isSidePanelVisible },
    tabs: { tabData },
    lineActions: { reassignConfigMap },
    viewControls: { filterChangeCounter },
    bulkAction: { bulkActionProgress },
  } = useSelector((state) => state?.certifications?.details);
  const activeTab = tabData.find((tab) => tab.isActive);
  const { messages } = useSelector((state) => state?.user);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;
  const { isSecondaryCertifierLoggedIn } = reassignConfigMap;

  // Get redux responsive media hook for certs package
  useResponsiveValueRedux(updateScreenSize, SCREEN_WIDTH_QUERIES);
  const queryParams = new URLSearchParams(location?.search);
  const certKey = queryParams.get('certkey');

  const alertWrapperClasses = cx(
    'Certs-AlertBannerProvider',
    isSidePanelVisible && 'Certs-AlertBannerProvider--isSidePanelVisible'
  );
  const pageClasses = cx(
    'CertificationDetails',
    isSidePanelVisible && 'CertificationDetails--isSidePanelVisible',
    isDraggingDndItem && 'CertificationDetails--isDraggingDndItem',
    bulkActionProgress?.inProgress && 'CertificationDetails--bulkInProgress'
  );

  // Start or stop polling the bulkActionProgress API based on inProgress flag
  useEffect(() => {
    if (bulkActionProgress?.inProgress) {
      setShowFilterBar(false);
      dispatch(setSidePanelVisible(false)); // edge case 5: close open side panel
      bulkActionPollingFailuresRef.current = 0;

      // edge case 2: fire one poll immediately so the UI doesn't sit at 0% for 5 s
      dispatch(fetchBulkActionProgress(certKey)).catch(() => {});

      bulkActionPollingIntervalRef.current = setInterval(() => {
        dispatch(fetchBulkActionProgress(certKey))
          .then(() => {
            bulkActionPollingFailuresRef.current = 0;
          })
          .catch(() => {
            bulkActionPollingFailuresRef.current += 1;

            if (bulkActionPollingFailuresRef.current >= 3) {
              clearInterval(bulkActionPollingIntervalRef.current);
              bulkActionPollingIntervalRef.current = null;
              dispatch(
                setBulkActionFailed(
                  'Unable to check processing status. Please refresh to see the latest data.'
                )
              );
            }
          });
      }, 5000);
    } else if (bulkActionPollingIntervalRef.current) {
      clearInterval(bulkActionPollingIntervalRef.current);
      bulkActionPollingIntervalRef.current = null;
    }

    return () => {
      clearInterval(bulkActionPollingIntervalRef.current);
    };
  }, [bulkActionProgress?.inProgress]);

  // Refresh tab counts and list data when bulk action processing completes
  useEffect(() => {
    const inProgress = bulkActionProgress?.inProgress;

    if (prevBulkInProgressRef.current && !inProgress) {
      dispatch(setBulkActionInProgress(false)); // reset percentage/failed/state after completion or failure
      dispatch(getTabCounts(certKey, true));
      dispatch(setCertTriggerFromSearchOrFilter(true));
      dispatch(
        getCertDetailsData(
          certKey,
          columnNames,
          getItemsApiOffset(activeTab?.count),
          0,
          searchText
        )
      );
      dispatch(getProgressBreakdownDetails(certKey, BY_DECISION, true));
    }

    prevBulkInProgressRef.current = inProgress;
  }, [bulkActionProgress?.inProgress]);

  /**
   * CR-21415: Resets visibleItemRange when mergedLists is cleared (e.g., tab switch, search, filter).
   * Ensures the "Showing Records" count does not display stale data.
   */
  useEffect(() => {
    if (mergedLists?.length === 0) {
      setVisibleItemRange({ start: 1, end: 0 });
    }
  }, [mergedLists]);

  useEffect(() => {
    dispatch(updateTouchDevice(isTouchDevice));

    const windowMatchMediaLandscape = window.matchMedia(
      '(orientation: landscape) and (max-height: 659px)'
    );

    // Check for the landscape mode
    const checkLandscapeMode = (e) => {
      if (isTouchDevice) {
        setIsPotrait(!e.matches);
        setIsPotraitModalOpen(e.matches);
      }
    };

    windowMatchMediaLandscape.addEventListener('change', checkLandscapeMode);

    return () => {
      windowMatchMediaLandscape.removeEventListener(
        'change',
        checkLandscapeMode
      );
    };
  }, [isTouchDevice]);

  const personalizationPending = useRef(false);

  useEffect(() => {
    if (certKey) {
      // calling clear certs session
      dispatch(clearCertsSession(certKey))
        .then(() => {})
        .finally(() => {
          const promiseArrayToCheckEssentialAPIs = [
            dispatch(getViewDetails(certKey)),
            dispatch(checkEsConnection(certKey)),
          ];

          Promise.allSettled(promiseArrayToCheckEssentialAPIs).then(
            async (response) => {
              const viewApiStatus = response[0].status;
              const esApisStatus = response[1].status;

              if (
                viewApiStatus !== PROMISE_REJECTED &&
                esApisStatus !== PROMISE_REJECTED
              ) {
                dispatch(setCertKeyInUI(certKey));

                // getViewDetails now returns { campaignKey, bulkActionInProgress, newCertUX }
                const resolvedResult = response[0].value;
                const resolvedCampaignKey = resolvedResult?.campaignKey;
                const isBulkActionInProgress =
                  resolvedResult?.bulkActionInProgress ?? false;

                if (resolvedResult?.newCertUX === '0') {
                  setIsClassicCampaignModalOpen(true);

                  return;
                }

                // await column picker so flattenedPickerList is available before getPersonalization
                const flattenedPickerList = await Promise.resolve(
                  dispatch(
                    getColumnPickerListDetails(resolvedCampaignKey, certKey)
                  )
                );
                // derive comma-separated column names string directly from flattenedPickerList
                const columnNamesFromPicker = Object.keys(
                  flattenedPickerList || {}
                ).join(',');

                personalizationPending.current = true;

                const personalizationStatus = await Promise.resolve(
                  dispatch(getPersonalization())
                );

                if (
                  personalizationStatus ===
                  PERSONALIZATION_STATUS.NO_PERSONALIZATION
                ) {
                  personalizationPending.current = false;
                  dispatch(setDefaultRowFilters());

                  // edge case 6: skip items call during UC2 hard-reload bulk action
                  if (columnNamesFromPicker && !isBulkActionInProgress) {
                    dispatch(
                      getCertDetailsData(
                        certKey,
                        columnNamesFromPicker,
                        getItemsApiOffset(activeTab?.count),
                        0,
                        searchText
                      )
                    );
                  }
                } else if (
                  personalizationStatus !==
                  PERSONALIZATION_STATUS.EXIST_PERSONALIZATION
                ) {
                  personalizationPending.current = false;
                }

                dispatch(getPredefinedComments(certKey)).catch((error) =>
                  Logger.log(error)
                );
              }

              if (
                viewApiStatus !== PROMISE_REJECTED &&
                esApisStatus === PROMISE_REJECTED
              ) {
                setIsErrorModalOpen(true);
              }
            }
          );

          const timeZone = momentTimezone.tz.guess();
          const timeZoneOffset = new Date().getTimezoneOffset();
          const timeZoneAbbrevation = momentTimezone.tz
            .zone(timeZone)
            .abbr(timeZoneOffset);

          dispatch(setCertsTimeZone(timeZoneAbbrevation));
        });
    }
  }, [certKey]);

  // Get the row filter applied values
  const personalizationViewOption = personalization?.viewOption?.view;
  const personalizedRowsFilters = personalization?.personalizedData?.result
    ?.info?.defaultView?.[0]
    ? personalization.personalizedData.result.info.defaultView[0].rowsFilters
    : undefined;
  const clearCurrentPersonalization =
    personalization?.clearCurrentPersonalization;

  const onRowsUpdate = () => {
    // Release the personalization guard so the triggerFilterChange below
    // is allowed to proceed through the filterChangeCounter effect.
    personalizationPending.current = false;

    if (personalizationViewOption === VIEW_OPTIONS.DEFAULT_VIEW) {
      // Build default row filters by selecting only the rows that are
      // present in viewRowsList (the allowed objects for this campaign).
      // Uses the EO-specific or standard mapping based on campaign type.
      const defaultRowFilters = Object.entries(
        isEOCampaign ? EO_UPDATE_ROW_FILTER_MAPPING : UPDATE_ROW_FILTER_MAPPING
      )
        ?.filter(([, label]) => {
          // Map the display label (e.g. 'User') to its object key (e.g. 'users')
          // and check if it exists in the allowed viewRowsList
          if (isEOCampaign) {
            return viewRowsList?.includes(
              EO_UPDATE_ROW_FILTER_MAPPING_WITH_OBJECTS_TO_BE_INCLUDED[label]
            );
          }

          return viewRowsList?.includes(
            UPDATE_ROW_FILTER_MAPPING_WITH_OBJECTS_TO_BE_INCLUDED[label]
          );
        })
        .map(([key]) => key); // Extract only the value keys (e.g. '-4', '-2')

      dispatch(setSelectedRowFilters(defaultRowFilters));
      dispatch(setTriggerForUserDefaultView(false));
      dispatch(triggerFilterChange());

      return;
    }

    const totalRowFiltersToApply = [];
    const rowFilters = personalizedRowsFilters?.map((ele) =>
      parseStringValue(ele)
    );
    const rowFiltersToUpdate = Object.entries(
      isEOCampaign ? EO_UPDATE_ROW_FILTER_MAPPING : UPDATE_ROW_FILTER_MAPPING
    )
      ?.filter(([, label]) => {
        if (isEOCampaign) {
          return viewRowsList?.includes(
            EO_UPDATE_ROW_FILTER_MAPPING_WITH_OBJECTS_TO_BE_INCLUDED[label]
          );
        }

        return viewRowsList?.includes(
          UPDATE_ROW_FILTER_MAPPING_WITH_OBJECTS_TO_BE_INCLUDED[label]
        );
      })
      .map(([key]) => key);

    if (personalizationViewOption !== VIEW_OPTIONS.DEFAULT_VIEW) {
      const shouldApplyRowFilter = checkAllPersonalizedRowsExist(
        isEOCampaign,
        viewRowsList,
        rowFilters
      );

      if (shouldApplyRowFilter) {
        if (isEOCampaign) {
          totalRowFiltersToApply.push(
            ...rowFilters.map(
              (row) => EO_UPDATE_ROW_FILTER_MAPPING?.[parseStringValue(row)]
            )
          );
        } else {
          totalRowFiltersToApply.push(
            ...rowFilters.map(
              (row) => UPDATE_ROW_FILTER_MAPPING?.[parseStringValue(row)]
            )
          );
        }

        dispatch(updatePersonalizationAll({ rows: true }));
      } else {
        dispatch(
          setPersonalizationViewOption({
            view: VIEW_OPTIONS.DEFAULT_VIEW,
            applied: false,
          })
        );
        dispatch(updatePersonalizationAll({ rows: false }));
      }
    }

    if (personalization?.isDefaultTriggeredByUser) {
      totalRowFiltersToApply.push(...rowFiltersToUpdate);
      dispatch(setTriggerForUserDefaultView(false));
    }

    if (totalRowFiltersToApply?.length >= 1) {
      dispatch(setSelectedRowFilters(rowFilters));

      dispatch(triggerFilterChange());
    } else {
      dispatch(setSelectedRowFilters(rowFiltersToUpdate));
      dispatch(triggerFilterChange());
    }

    totalRowFiltersToApply.length = 0;
  };

  const isPersonalizedAdvancedFilters =
    personalization?.personalizationAll.advancedFilters;
  const isPersonalizedRows = personalization?.personalizationAll.rows;
  const isPersonalizedColumns = personalization?.personalizationAll.columns;

  useEffect(() => {
    if (
      (isPersonalizedAdvancedFilters &&
        isPersonalizedRows &&
        isPersonalizedColumns) ||
      personalizationViewOption === VIEW_OPTIONS.DEFAULT_VIEW
    ) {
      onRowsUpdate();
    }
  }, [
    isPersonalizedAdvancedFilters,
    isPersonalizedRows,
    isPersonalizedColumns,
    personalizationViewOption,
  ]);

  useEffect(() => {
    if (clearCurrentPersonalization) {
      onRowsUpdate();
    }
  }, [clearCurrentPersonalization]);

  const isFilterInitialMount = useRef(true);

  useEffect(() => {
    if (isFilterInitialMount.current) {
      isFilterInitialMount.current = false;

      return;
    }

    if (personalizationPending.current) return;

    // Suppress "no data" flash during rapid filter changes by signaling a
    // search/filter transition. This triggers isTransitioningRef=true in
    // CertsDetailsList, keeping the empty-state view hidden until API responds.
    dispatch(setCertTriggerFromSearchOrFilter(true));
    setMergedLists([]);

    // offset reset not required here, merged is doing empty to show loader
    if (certKey && columnNames) {
      dispatch(
        getCertDetailsData(
          certKey,
          columnNames,
          getItemsApiOffset(activeTab?.count),
          0,
          searchText
        )
      );
    }
  }, [filterChangeCounter]);

  const showHideSaviCompanion = (isShow) => {
    setShowSaviCompanion(isShow);

    if (showSaviLocal) {
      setShowSaviCompanionButtonPopup(true);
      cookies.set(AUTO_SHOW_SAVI_COMPANION, '0');
    }
  };

  const getNodeByDirection = (
    nodes,
    currentNodeId,
    jumpToSameLevel,
    direction
  ) => {
    const isDirectionDown = direction === MODAL_SIDEPANEL_DIRECTION.DOWN;
    const isDirectionUp = direction === MODAL_SIDEPANEL_DIRECTION.UP;
    const flattenedList = flattenList(nodes);

    const currentNodeIndex = flattenedList.findIndex(
      (node) => node?.tracker?.id === currentNodeId
    );

    if (currentNodeIndex === -1) return null;
    if (currentNodeIndex === flattenedList.length - 1 && isDirectionDown)
      return null;

    // Handle navigation downwards
    if (isDirectionDown) {
      for (let i = currentNodeIndex + 1; i < flattenedList.length; i += 1) {
        const isEndPoint = Boolean(flattenedList[i]?.isEndPoint);

        if (!isEndPoint) {
          if (jumpToSameLevel) {
            if (
              flattenedList[currentNodeIndex]?.tracker?.level >=
              flattenedList[i]?.tracker?.level
            ) {
              return flattenedList[i];
            }
          } else {
            return flattenedList[i];
          }
        }
      }
    }

    // Handle navigation upwards
    if (isDirectionUp) {
      for (let i = currentNodeIndex - 1; i >= 0; i -= 1) {
        const isEndPoint = Boolean(flattenedList[i]?.isEndPoint);

        if (!isEndPoint) {
          if (jumpToSameLevel) {
            if (
              flattenedList[currentNodeIndex]?.tracker?.level >=
              flattenedList[i]?.tracker?.level
            ) {
              return flattenedList[i];
            }
          } else {
            return flattenedList[i];
          }
        }
      }
    }

    return null;
  };

  /**
   * Updates the `isConnected` property of nodes in a hierarchical structure.
   *
   * This function recursively traverses the provided list of nodes and updates the `isConnected` property
   * of the node with the specified `nodeToUpdateId` to `true`. All other nodes will have their `isConnected`
   * property set to `false`.
   *
   * @param {Array} prev - The previous list of nodes (merged list) to be updated.
   * @param {string} nodeToUpdateId - The trackerId of the node to be marked as connected.
   * @returns {Array} - The updated list of nodes (merged list) with the `isConnected` property updated accordingly.
   */
  const updateIsConnected = (prev, nodeToUpdateId) => {
    const data = prev.map((node) => {
      if (node.tracker.id === nodeToUpdateId) {
        return {
          ...node,
          tracker: {
            ...node.tracker,
            isConnected: true,
          },
          children:
            node?.children?.length > 0
              ? updateIsConnected(node?.children, nodeToUpdateId)
              : [],
        };
      }

      return {
        ...node,
        tracker: {
          ...node.tracker,
          isConnected: false,
        },
        children:
          node?.children?.length > 0
            ? updateIsConnected(node?.children, nodeToUpdateId)
            : [],
      };
    });

    return data;
  };

  const handleSidePanelBreadCrumbAction = (level) => {
    if (isSidePanelVisible && sidePanelData?.tracker?.id) {
      const nodes = Array.isArray(mergedListsAfterRowFilter)
        ? mergedListsAfterRowFilter
        : [mergedListsAfterRowFilter];
      const currentNodeId = sidePanelData?.tracker?.id;
      const currentParentCount = sidePanelData?.tracker?.parents?.length;
      const currentNodeParents = findHierarchyElementsV2(nodes, currentNodeId);

      let nextNode = null;

      if (currentParentCount === 2 && level === 1)
        nextNode = currentNodeParents[1]?.nodeData;
      if (currentParentCount === 2 && level === 2)
        nextNode = currentNodeParents[0]?.nodeData;
      if (currentParentCount === 1 && level === 1)
        nextNode = currentNodeParents[0]?.nodeData;

      if (nextNode) {
        if (sidePanelData?.tracker?.id !== nextNode?.tracker?.id) {
          setSidePanelData(nextNode);
          setMergedLists((prev) =>
            updateIsConnected(prev, nextNode?.tracker?.id)
          );
        }
      }
    }
  };

  const handleSidePanelTileButton = (currentNodeId, direction) => {
    const nodes = Array.isArray(mergedListsAfterRowFilter)
      ? mergedListsAfterRowFilter
      : [mergedListsAfterRowFilter];
    const nextNode = getNodeByDirection(nodes, currentNodeId, false, direction);

    if (nextNode) {
      if (sidePanelData?.tracker?.id !== nextNode?.tracker?.id) {
        setSidePanelData(nextNode);
        setMergedLists((prev) =>
          updateIsConnected(prev, nextNode?.tracker?.id)
        );
      }
    }
  };

  /**
   * Handles the action for the side panel, such as navigating to the next node in the list.
   * It updates the side panel data and the merged lists based on the provided parameters.
   *
   * @param {boolean} jumpToSameLevel - If true, the function will jump to the next node at the same level.
   * @param {function} callback - A callback function to be executed after the side panel action is handled.
   *                              The callback receives an object with the updated lists and the next node.
   *                              If no next node is found, the nextNode property will be false, and sidepanel
   *                              will be closed if open.
   *
   * @returns {void} - This function does not return a value.
   */
  const handleSidePanelAction = useCallback(
    (jumpToSameLevel, callback) => {
      if (isSidePanelVisible && sidePanelData?.tracker?.id) {
        const nodes = Array.isArray(mergedListsAfterRowFilter)
          ? mergedListsAfterRowFilter
          : [mergedListsAfterRowFilter];
        const currentNodeId = sidePanelData?.tracker?.id;
        const nextNode = getNodeByDirection(
          nodes,
          currentNodeId,
          jumpToSameLevel,
          MODAL_SIDEPANEL_DIRECTION.DOWN
        );

        if (nextNode) {
          if (sidePanelData?.tracker?.id !== nextNode?.tracker?.id) {
            setSidePanelData(nextNode);
            setMergedLists((prev) => {
              const updatedLists = updateIsConnected(
                prev,
                nextNode?.tracker?.id
              );

              if (callback) callback({ updatedLists, nextNode });

              return updatedLists;
            });

            return;
          }
        }

        setMergedLists((prev) => {
          if (callback) callback({ updatedLists: prev, nextNode: false });

          return prev;
        });
        dispatch(setSidePanelVisible(false));
      }
    },
    [isSidePanelVisible, sidePanelData, mergedListsAfterRowFilter]
  );

  // Disable tab switching while card transition is happening
  useEffect(() => {
    if (activeTab.key !== TAB_STATUS_ALL) {
      if (timeoutIds?.length > 0) {
        dispatch(setTabsDisabled(true));
      } else {
        dispatch(setTabsDisabled(false));
      }
    }
  }, [timeoutIds, activeTab]);

  useEffect(() => {
    setTimeoutIds([]);
  }, [activeTab]);

  // Helper function to find node by EID and keys mapping
  const findNodeByEidAndKeys = (
    nodeList,
    topmostParentEid,
    eidsAndKeysMap,
    isEOCampaignType
  ) =>
    nodeList.find((topLevelNode) => {
      const topMostNodeEidMap = eidsAndKeysMap[0] || {};

      if (topMostNodeEidMap?.isEidAvailable)
        return topLevelNode?.id === topmostParentEid;

      if (isEOCampaignType) {
        if (topMostNodeEidMap?.valueKey === '0') {
          return topLevelNode?.CERT_ENTITLEMENT_VALUEKEY === topmostParentEid;
        }

        if (
          topMostNodeEidMap?.valueKey === EO_GROUP_BY_CERT_ENTITLEMENT_VALUEKEY
        ) {
          return topLevelNode?.CERT_ENTITLEMENT_VALUEKEY === topmostParentEid;
        }
      } else {
        if (topMostNodeEidMap?.valueKey === '-4')
          return topLevelNode?.CERT_USERKEY === topmostParentEid;

        if (topMostNodeEidMap?.valueKey === '0')
          return topLevelNode?.CERT_ACCOUNTKEY === topmostParentEid;
      }

      return topLevelNode?.id === topmostParentEid;
    });

  /**
   * Updates a list of nodes (merged list) based on changes to associated nodes (line items), and adjusts the state and UI accordingly.
   *
   * This function performs the following steps:
   * 1. Finds and updates a topmost parent node if `topmostParentEid` is provided.
   * 2. Adjusts the offset for pagination based on changes in the updated and original nodes.
   * 3. Updates the merged list of nodes with the changes applied to the original parent node.
   * 4. If `topmostParentEid` is not provided, updates the merged list with changes to a single node.
   *
   * @param {Array} listToUpdate - The list of nodes (merged list) that needs to be updated.
   * @param {Array} updatedNodes - The nodes with updated data.
   * @param {string} actionKind - Type of action to perform (e.g., 'approve', 'reject', 'doesnotworkforme').
   * @param {Object} currentState - The current state of the action (actionKind: Select4ed or unselected).
   * @param {string} trackerId - Identifier used for tracking changes.
   * @param {string} [topmostParentEid] - Optional identifier for the topmost parent node to update.
   * @param {Array} eidsToUpdate - List of EIDs (Entity IDs) that need to be updated.
   * @param {Object} eidsAndKeysMap - Map containing EID and key configurations for node identification.
   */
  const updateMergedListsForAssociatedNodes = async (
    isEOCampaignType,
    listToUpdate,
    updatedNodes,
    actionKind,
    currentState,
    trackerId,
    topmostParentEid,
    eidsToUpdate,
    eidsAndKeysMap
  ) => {
    if (topmostParentEid) {
      const originalNodeToUpdate = findNodeByEidAndKeys(
        listToUpdate,
        topmostParentEid,
        eidsAndKeysMap,
        isEOCampaignType
      );

      const updatedOriginalParentNode = updateExistingNodes(
        isEOCampaign,
        originalNodeToUpdate ? [originalNodeToUpdate] : [],
        updatedNodes,
        actionKind,
        currentState,
        trackerId,
        eidsToUpdate
      );

      const flattenedListUpdated = flattenList(updatedOriginalParentNode);
      const flattenedListOriginal = flattenList(
        originalNodeToUpdate ? [originalNodeToUpdate] : []
      );

      const updatedCount = flattenedListUpdated.filter(
        (node) => node?.rowNotToShow && node.rowNotToShow === '0'
      )?.length;
      const originalCount = flattenedListOriginal.filter(
        (node) => node?.rowNotToShow && node.rowNotToShow === '0'
      )?.length;

      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(Math.abs(originalCount - updatedCount))) {
        const countToUpdate = Math.max(
          0,
          currentOffset - Math.abs(originalCount - updatedCount)
        );

        if (countToUpdate === 0) {
          dispatch(setSidePanelVisible(false));
          setMergedLists([]);
          setCurrentOffset(getItemsApiOffset(activeTab?.count));
          dispatch(
            getCertDetailsData(
              certKey,
              columnNames,
              getItemsApiOffset(activeTab?.count),
              0,
              searchText
            )
          );
        } else {
          setCurrentOffset(
            countToUpdate || getItemsApiOffset(activeTab?.count)
          );
        }
      }

      await new Promise((resolve) => {
        const mergedListsChangeTimeOutId = setTimeout(() => {
          setMergedLists((currentList) => {
            const currentNodeToUpdate = findNodeByEidAndKeys(
              currentList,
              topmostParentEid,
              eidsAndKeysMap,
              isEOCampaignType
            );

            // Recalculate with current node
            const currentUpdatedParentNode = updateExistingNodes(
              isEOCampaign,
              currentNodeToUpdate ? [currentNodeToUpdate] : [],
              updatedNodes,
              actionKind,
              currentState,
              trackerId,
              eidsToUpdate
            );

            const isValidUpdatedOriginalParentNode = checkIfValidNode(
              currentUpdatedParentNode
            );

            if (
              currentUpdatedParentNode?.length === 0 ||
              !isValidUpdatedOriginalParentNode
            ) {
              return currentList?.filter((node) => {
                if (node?.id) return node?.id !== topmostParentEid;

                if (isEOCampaignType) {
                  if (node?.ROW_TYPE?.toString() === '0')
                    return node?.CERT_ENTITLEMENT_VALUEKEY !== topmostParentEid;

                  if (
                    node?.ROW_TYPE?.toString() ===
                    EO_GROUP_BY_CERT_ENTITLEMENT_VALUEKEY
                  )
                    return node?.CERT_ENTITLEMENT_VALUEKEY !== topmostParentEid;
                } else {
                  if (node?.CERT_ENTITLEMENT_VALUEKEY === '-4')
                    return node?.CERT_USERKEY !== topmostParentEid;

                  if (node?.CERT_ENTITLEMENT_VALUEKEY === '0')
                    return node?.CERT_ACCOUNTKEY !== topmostParentEid;
                }

                return node?.id !== topmostParentEid;
              });
            }

            return currentList?.map((node) => {
              if (node?.id && node?.id === currentUpdatedParentNode[0]?.id)
                return currentUpdatedParentNode[0];

              if (isEOCampaignType) {
                if (
                  !node?.id &&
                  node?.ROW_TYPE?.toString() === '0' &&
                  node?.CERT_ENTITLEMENT_VALUEKEY ===
                    currentUpdatedParentNode[0]?.CERT_ENTITLEMENT_VALUEKEY
                )
                  return currentUpdatedParentNode[0];

                if (
                  !node?.id &&
                  node?.ROW_TYPE?.toString() ===
                    EO_GROUP_BY_CERT_ENTITLEMENT_VALUEKEY &&
                  node?.CERT_ENTITLEMENT_VALUEKEY ===
                    currentUpdatedParentNode[0]?.CERT_ENTITLEMENT_VALUEKEY
                )
                  return currentUpdatedParentNode[0];
              } else {
                if (
                  !node?.id &&
                  node?.CERT_ENTITLEMENT_VALUEKEY === '-4' &&
                  node?.CERT_USERKEY ===
                    currentUpdatedParentNode[0]?.CERT_USERKEY
                )
                  return currentUpdatedParentNode[0];

                if (
                  !node?.id &&
                  node?.CERT_ENTITLEMENT_VALUEKEY === '0' &&
                  node?.CERT_ACCOUNTKEY ===
                    currentUpdatedParentNode[0]?.CERT_ACCOUNTKEY
                )
                  return currentUpdatedParentNode[0];
              }

              return node;
            });
          });
        }, 0);

        setTimeout(() => {
          resolve();
        }, 500);

        setTimeoutIds((prevIds) => [...prevIds, mergedListsChangeTimeOutId]);
      });

      return;
    }

    await new Promise((resolve) => {
      const singleNodeTimeoutId = setTimeout(() => {
        setMergedLists((currentList) =>
          updateExistingSingleNode(
            currentList,
            actionKind,
            trackerId,
            currentState
          )
        );
      }, 0);

      setTimeout(() => {
        resolve();
      }, 500);

      setTimeoutIds((prevIds) => [...prevIds, singleNodeTimeoutId]);
    });
  };

  /**
   * `useEffect` hook that updates the merged list and side panel actions data
   * when `sidePanelData` changes and the side panel is visible.
   *
   * This effect performs the following actions:
   * 1. Checks if the side panel is visible (`isSidePanelVisible`).
   * 2. If the side panel is visible, it updates the merged list of nodes based on the `trackerId` from `sidePanelData`.
   * 3. Calls `updateNodeWithTrackerId` function to find the node in the merged list that matches the `trackerId`.
   * 4. Sets the actions data for the side panel using `setSidePanelActionsData` with the matching node.
   *
   * Dependencies:
   * - `sidePanelData`: The data associated with the side panel, which includes the tracker ID.
   * - `isSidePanelVisible`: Boolean indicating if the side panel is currently visible.
   *
   * The `useEffect` hook is triggered whenever `sidePanelData` changes.
   */
  useEffect(() => {
    if (isSidePanelVisible)
      setMergedLists((prev) =>
        updateNodeWithTrackerId(
          prev,
          sidePanelData?.tracker?.id,
          (matchingNode) => {
            setSidePanelActionsData(matchingNode);
          }
        )
      );
  }, [sidePanelData]);

  // Close side panel if the currently viewed record is no longer in the filtered list
  useEffect(() => {
    if (!isSidePanelVisible || !sidePanelData?.tracker?.id) return;

    const nodes = Array.isArray(mergedListsAfterRowFilter)
      ? mergedListsAfterRowFilter
      : [mergedListsAfterRowFilter];
    const flatList = flattenList(nodes);
    const recordExists = flatList.some(
      (node) => node?.tracker?.id === sidePanelData.tracker.id
    );

    if (!recordExists) {
      dispatch(setSidePanelVisible(false));
    }
  }, [mergedListsAfterRowFilter, isSidePanelVisible, sidePanelData]);

  /**
   * Handles the automatic selection of a parent node and updates the side panel and merged lists accordingly.
   *
   * This function performs the following actions:
   * 1. Retrieves tracking and state (selected or unselected action button) information from the `referenceObject`.
   * 2. Determines the hierarchy to search for based on the tracker level.
   * 3. If the side panel is visible and certain conditions are met, it updates the merged list and hierarchy elements.
   * 4. Refreshes merged list based on the updated hierarchy and EIDs.
   * 5. Updates the merged list with new node data and handles various UI updates and side panel actions.
   * 6. Dispatches additional actions based on the current state and action type.
   *
   * @param {Object} referenceObject - Contains tracking information and flags used for processing.
   * @param {Object} nonReferenceObject - Contains additional data for UI updates and actions.
   */
  const processAutoSelectParent = async (
    referenceObject,
    nonReferenceObject
  ) => {
    const trackerId = referenceObject?.trackerId;
    const trackerLevel = referenceObject?.trackerLevel;
    const trackerActionKind = referenceObject?.actionKind;
    const trackerCurrentState = referenceObject?.currentState;
    const sidePanelFlags = referenceObject?.flags;
    // isAccount: true when the line item is an Account (CERT_ENTITLEMENT_VALUEKEY === '0')
    // passed from CertsActionButtonsGroup to avoid re-traversing mergedLists here
    const isAccount = referenceObject?.isAccount;

    const hierarchyToFind = getTypeOfHierarchy(trackerLevel);
    let hierarchyElements;
    let mergedListsForAutoSelect = mergedLists;
    let trackerIdForActions = trackerId;

    // Move to next Tile on action before the data is updated
    if (isSidePanelVisible && sidePanelFlags?.shouldSwitchSidePanel) {
      await new Promise((resolve) => {
        handleSidePanelAction(
          sidePanelFlags?.jumpToSameLevel,
          ({ updatedLists, nextNode }) => {
            mergedListsForAutoSelect = updatedLists;
            trackerIdForActions = nextNode?.tracker?.id;
            hierarchyElements = findHierarchyElementsV2(
              updatedLists,
              trackerId,
              hierarchyToFind
            );
            resolve();
          }
        );
      });
    } else {
      if (activeTab.key === TAB_STATUS_DONE)
        dispatch(setSidePanelVisible(false));
      hierarchyElements = findHierarchyElementsV2(
        mergedLists,
        trackerId,
        hierarchyToFind
      );
    }

    const eidsAndKeysMapList = hierarchyElements?.map((flattenedNode) => {
      const tempObject = {
        isEidAvailable: true,
        valueKey: flattenedNode?.nodeData?.CERT_ENTITLEMENT_VALUEKEY,
        id: flattenedNode?.nodeData?.id,
      };

      if (isEOCampaign) {
        if (
          !flattenedNode?.nodeData?.id &&
          flattenedNode?.nodeData?.ROW_TYPE.toString() === '0'
        ) {
          tempObject.isEidAvailable = false;
          tempObject.valueKey = flattenedNode?.nodeData?.ROW_TYPE.toString();
          tempObject.id = flattenedNode?.nodeData?.CERT_ENTITLEMENT_VALUEKEY;
        }

        if (
          !flattenedNode?.nodeData?.id &&
          flattenedNode?.nodeData?.ROW_TYPE.toString() ===
            EO_GROUP_BY_CERT_ENTITLEMENT_VALUEKEY
        ) {
          tempObject.isEidAvailable = false;
          tempObject.valueKey = flattenedNode?.nodeData?.ROW_TYPE.toString();
          tempObject.id = flattenedNode?.nodeData?.CERT_ENTITLEMENT_VALUEKEY;
        }
      } else {
        if (
          !flattenedNode?.nodeData?.id &&
          flattenedNode?.nodeData?.CERT_ENTITLEMENT_VALUEKEY === '-4'
        ) {
          tempObject.isEidAvailable = false;
          tempObject.valueKey =
            flattenedNode?.nodeData?.CERT_ENTITLEMENT_VALUEKEY;
          tempObject.id = flattenedNode?.nodeData?.CERT_USERKEY;
        }

        if (
          !flattenedNode?.nodeData?.id &&
          flattenedNode?.nodeData?.CERT_ENTITLEMENT_VALUEKEY === '0'
        ) {
          tempObject.isEidAvailable = false;
          tempObject.valueKey =
            flattenedNode?.nodeData?.CERT_ENTITLEMENT_VALUEKEY;
          tempObject.id = flattenedNode?.nodeData?.CERT_ACCOUNTKEY;
        }
      }

      return tempObject;
    });

    const arrayForEidsToRefresh = eidsAndKeysMapList
      ?.filter((mapObject) => mapObject?.isEidAvailable)
      ?.map((mapObject) => mapObject?.id);
    const topmostLevelEid = eidsAndKeysMapList[0]?.id || null;

    await dispatch(getRefreshedCertDetails(arrayForEidsToRefresh))
      .then(async (responses) => {
        await updateMergedListsForAssociatedNodes(
          isEOCampaign,
          mergedListsForAutoSelect,
          responses,
          trackerActionKind,
          trackerCurrentState,
          trackerId,
          topmostLevelEid,
          arrayForEidsToRefresh,
          eidsAndKeysMapList
        );
      })
      .catch((error) => {
        setMergedLists((prev) =>
          updateExistingSingleNode(
            prev,
            trackerActionKind,
            trackerId,
            trackerCurrentState
          )
        );
        if (
          (ACTION_BUTTON_KINDS.REASSIGN !== trackerActionKind ||
            ACTION_BUTTON_KINDS.CONSULT !== trackerActionKind) &&
          !isSecondaryCertifierLoggedIn &&
          !backNavigation?.consulteeLoggedIn
          // to ignore error for secondary certifier and consultee
        )
          dispatch(setBulkActionAlert(true, error.message));
      })
      .finally(() => {
        const shouldRunItemsApiFromStart =
          Boolean(
            // Refresh list from start when action is Terminate or Decommission,
            // OR when it is a UM campaign account line item being actioned on the Done tab
            // (account items don't move between tabs, so list must reload from scratch)
            (trackerActionKind === ACTION_BUTTON_KINDS.TERMINATE ||
              trackerActionKind === ACTION_BUTTON_KINDS.DECOMMISSION ||
              (!isEOCampaign &&
                isAccount &&
                trackerActionKind === ACTION_BUTTON_KINDS.REJECT)) && // UM , SA, AO and self campaign account line item
              activeTab.key === TAB_STATUS_DONE && // only on Done tab
              !trackerCurrentState // only on initial action, not reset
          ) ||
          Boolean(
            // For all reset actions (undoing a previous decision) — refresh the current tab
            // except Extend End Date and Consult which don't change item position
            trackerCurrentState &&
              trackerActionKind !== ACTION_BUTTON_KINDS.EXTEND_END_DATE &&
              trackerActionKind !== ACTION_BUTTON_KINDS.CONSULT &&
              (activeTab.key === TAB_STATUS_DONE ||
                activeTab.key === TAB_STATUS_TODO ||
                activeTab.key === TAB_STATUS_CONS)
          );

        if (shouldRunItemsApiFromStart) {
          dispatch(setSidePanelVisible(false));
          setMergedLists([]);
          setCurrentOffset(getItemsApiOffset(activeTab?.count));
          dispatch(
            getCertDetailsData(
              certKey,
              columnNames,
              getItemsApiOffset(activeTab?.count),
              0,
              searchText
            )
          );
        }

        const alertBannerPayload = nonReferenceObject?.alertBannerPayload;
        const tabToHighLightPayload = nonReferenceObject?.tabToHighLightPayload;

        const handleSidePanelActionByState = () => {
          if (isSidePanelVisible)
            setMergedLists((prev) =>
              updateNodeWithTrackerId(
                prev,
                trackerIdForActions,
                (matchingNode) => {
                  setSidePanelActionsData(matchingNode);
                }
              )
            );
        };

        if (trackerCurrentState) handleSidePanelActionByState();

        const finalDispatchActionTimeoutId = setTimeout(() => {
          // All the actions which need to be performed after the action API is successful
          if (!trackerCurrentState) handleSidePanelActionByState();

          dispatch(
            getTabCounts(
              certKey,
              ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME === trackerActionKind ||
                ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME ===
                  trackerActionKind ||
                ACTION_BUTTON_KINDS.REASSIGN === trackerActionKind ||
                ACTION_BUTTON_KINDS.CONSULT === trackerActionKind
            )
          ); // Get All count only for Reassign action

          // Update the total count
          dispatch(getTotalCount());

          if (tabToHighLightPayload?.shouldHighLightTab)
            dispatch(
              setTabHightLightKey(tabToHighLightPayload?.tabToHighLight)
            );
        }, 500);

        setTimeoutIds((prevIds) => [...prevIds, finalDispatchActionTimeoutId]);

        // No transition timeout needed for these functions
        if (alertBannerPayload?.shouldShowBanner) {
          dispatch(setBannerForConsultAndReassign(alertBannerPayload?.payload));
        }

        dispatch(getProgressBreakdownDetails(certKey, BY_DECISION, true));
      });
  };

  const runAutoSelectParent = async (referenceObject, nonReferenceObject) => {
    processingRef.current = true;

    try {
      await processAutoSelectParent(referenceObject, nonReferenceObject);
    } finally {
      processingRef.current = false;
    }
  };

  const clearAllActionLoading = (nodes) =>
    (Array.isArray(nodes) ? nodes : []).map((node) => {
      if (!node) return node;

      const clearedActions = node.tracker?.actions
        ? Object.entries(node.tracker.actions).reduce(
            (acc, [actionKey, actionValue]) => ({
              ...acc,
              [actionKey]: { ...actionValue, isLoading: false },
            }),
            {}
          )
        : node.tracker?.actions;

      return {
        ...node,
        tracker: node.tracker
          ? {
              ...node.tracker,
              actions: clearedActions,
            }
          : node.tracker,
        children:
          node.children?.length > 0 ? clearAllActionLoading(node.children) : [],
      };
    });

  // Process queue when mergedLists is updated
  useEffect(() => {
    if (processingRef.current || autoSelectQueue.length === 0) return undefined;
    const queueTimeoutId = setTimeout(() => {
      const nextItem = autoSelectQueue[0];

      setAutoSelectQueue((prev) => prev.slice(1));
      runAutoSelectParent(
        nextItem.referenceObject,
        nextItem.nonReferenceObject
      );
    }, 250);

    return () => clearTimeout(queueTimeoutId);
  }, [processingRef.current, autoSelectQueue]);

  // Safety: ensure no action stays stuck in loading once processing is idle
  useEffect(() => {
    if (!processingRef.current && autoSelectQueue.length === 0)
      setMergedLists((prev) => clearAllActionLoading(prev));
  }, [processingRef.current, autoSelectQueue.length]);

  // handleAutoSelectParent
  const handleAutoSelectParent = (referenceObject, nonReferenceObject) => {
    if (processingRef.current) {
      setAutoSelectQueue((prev) => [
        ...prev,
        { referenceObject, nonReferenceObject },
      ]);

      return;
    }

    runAutoSelectParent(referenceObject, nonReferenceObject);
  };

  const checkNodePosition = (currentNodeId) => {
    const nodes = Array.isArray(mergedListsAfterRowFilter)
      ? mergedListsAfterRowFilter
      : [mergedListsAfterRowFilter];

    const flattenedList = flattenList(nodes);
    const currentNodeIndex = flattenedList.findIndex(
      (node) => node?.tracker?.id === currentNodeId
    );

    const isPreviousDisabled =
      currentNodeIndex === 0 ||
      flattenedList
        ?.slice(0, currentNodeIndex)
        ?.every((node) => node?.isEndPoint);
    let isNextDisabled =
      currentNodeIndex === flattenedList.length - 1 ||
      flattenedList
        ?.slice(currentNodeIndex + 1)
        ?.every((node) => node?.isEndPoint);

    if (isMoreDataAvailable) {
      if (currentNodeIndex >= flattenedList.length - 2) {
        isNextDisabled = false;
        dispatch(setCertTriggerFromSearchOrFilter(false));

        if (currentOffset !== 0) {
          dispatch(
            getCertDetailsData(
              certKey,
              columnNames,
              getItemsApiOffset(activeTab?.count),
              currentOffset,
              searchText
            )
          );
        }

        setCurrentOffset(
          (prevOffset) => prevOffset + getItemsApiOffset(activeTab?.count)
        );
      }
    }

    return { isPreviousDisabled, isNextDisabled };
  };

  const esCheckAlertTitle =
    messages.CERTS_ES_CHECK_ALERT_TITLE ||
    localize(intl, getMessageKey('CERTS_ES_CHECK_ALERT_TITLE'));

  const esCheckAlertSubTitle =
    messages.CERTS_ES_CHECK_ALERT_SUBTITLE ||
    localize(intl, getMessageKey('CERTS_ES_CHECK_ALERT_SUBTITLE'));

  const classicCampaignAlertTitle = l(
    'Viewing unavailable — Classic Experience Campaign'
  );
  const classicCampaignAlertSubTitle = l(
    'This certification was launched in the classic experience. Detailed view cannot be displayed, but your campaign data is preserved and available to download.'
  );

  return (
    <AlertBannerProvider className={alertWrapperClasses}>
      <div className={pageClasses} data-testid='cert-details' ref={certPageRef}>
        <ModalIsland
          className='CertificationDetails-esCheckFailModal'
          kind='critical'
          alertTitle={esCheckAlertTitle}
          alertSubtitle={esCheckAlertSubTitle}
          onClose={() => {}}
          isOpen={isErrorModalOpen}
          primaryButton={null}
          secondaryButton={
            <Button
              type='button'
              kind='outlined'
              size='medium'
              isEnabled
              onClick={() => executeBackNavigation(backNavigation)}>
              Go Back
            </Button>
          }
        />
        <ModalIsland
          className='CertificationDetails-classicCampaignModal'
          kind='warning'
          alertTitle={classicCampaignAlertTitle}
          alertSubtitle={classicCampaignAlertSubTitle}
          onClose={() => {}}
          isOpen={isClassicCampaignModalOpen}
          primaryButton={null}
          secondaryButton={
            <Button
              type='button'
              kind='outlined'
              size='medium'
              isEnabled
              onClick={() => executeBackNavigation(backNavigation)}>
              Go Back
            </Button>
          }
        />
        <CertAlerts />
        <CertsDetailsExportAlerts />
        <CertsHeader />
        <CertsControlsGroup
          showFilterBar={showFilterBar}
          setShowFilterBar={
            bulkActionProgress?.inProgress ? () => {} : setShowFilterBar
          }
          setShowSaviCompanion={setShowSaviCompanion}
          showSaviCompanionButtonPopup={showSaviCompanionButtonPopup}
          setShowSaviCompanionButtonPopup={setShowSaviCompanionButtonPopup}
          filterBarRef={filterBarRef}
          controlsRef={controlsRef}
          filters={filters}
          setFilters={setFilters}
          setIsDraggingDndItem={setIsDraggingDndItem}
          setCurrentOffset={setCurrentOffset}
          setMergedLists={setMergedLists}
          visibleItemRange={visibleItemRange}
        />
        {/* not showing savi for SA campaign */}
        {isSaviEnabled &&
          showSavCompanion &&
          !isSACampaign &&
          !isSecondaryCertifierLoggedIn &&
          !backNavigation?.consulteeLoggedIn && (
            <SaviCompanion
              certKey={certKey}
              showSavCompanion={showSavCompanion}
              setShowSaviCompanion={showHideSaviCompanion}
            />
          )}
        <CertsDetailsList
          isMoreDataAvailable={isMoreDataAvailable}
          setIsMoreDataAvailable={setIsMoreDataAvailable}
          showFilterBar={showFilterBar}
          detailsListRef={detailsListRef}
          setSidePanelData={setSidePanelData}
          mergedLists={mergedLists}
          setMergedLists={setMergedLists}
          currentOffset={currentOffset}
          setCurrentOffset={setCurrentOffset}
          mergedListsAfterRowFilter={mergedListsAfterRowFilter}
          setMergedListsAfterRowFilter={setMergedListsAfterRowFilter}
          handleAutoSelectParent={handleAutoSelectParent}
          setVisibleItemRange={setVisibleItemRange}
        />
        {isSidePanelVisible && (
          <CertsSidePanel
            data={sidePanelData}
            actionsData={sidePanelActionsData}
            handleAutoSelectParent={handleAutoSelectParent}
            handleSidePanelTileButton={handleSidePanelTileButton}
            handleSidePanelBreadCrumbAction={handleSidePanelBreadCrumbAction}
            checkNodePosition={checkNodePosition}
            isOpen={isSidePanelVisible}
            setMergedLists={setMergedLists}
          />
        )}
        <CertsFooterActionBar />

        <Box className={!isPotrait ? 'CertificationDetails-whiteOverlay' : ''}>
          {isPotraitModalOpen && (
            <ModalIsland
              onClose={() => setIsPotraitModalOpen(false)}
              isOpen={isPotraitModalOpen}
              className='CertificationDetails-landscapeModal'
              classNames={{
                bodyClassName: 'CertificationDetails-landscapeModal--body',
                headerClassName: 'CertificationDetails-landscapeModal--header',
                sectionClassName:
                  'CertificationDetails-landscapeModal--section',
              }}
              overlayBackgroundColor='#8691a4'
              isCloseButtonVisible={false}
              contentBody={
                <>
                  <Lottie
                    className='CertificationDetails-landscapeModalIcon'
                    animationData={LandscapeModeIcon}
                  />
                  <Typography
                    kind='body1'
                    desktop='body1-d'
                    mobile='body1-d'
                    className='CertificationDetails-landscapeModalText'>
                    {messages.CERTS_PORTRAIT_MODE_MESSAGE ||
                      localize(
                        intl,
                        getMessageKey('CERTS_PORTRAIT_MODE_MESSAGE')
                      )}
                  </Typography>
                </>
              }
            />
          )}
        </Box>
      </div>
    </AlertBannerProvider>
  );
}

export default CertificationDetails;
